import {useCallback, useMemo} from "react";
import {OptionProps, SingleValue, MultiValue, components, MultiValueGenericProps} from "react-select";
import {useTranslation} from "react-i18next";

import CreatableSelect, {Option} from "../../ui/creatableselect";
import {
  useGetAccountCustomParametersQuery,
  useAddCustomParameterMutation,
  useUpdateCustomParameterMutation,
  useDeleteCustomParameterMutation,
} from "../../scenes/editor/challenge-edit.graphql";
import {useModal} from "../ModalProvider";
import CustomParameterEditModal from "./CustomParameterEditModal";
import IconButton from "../../ui/iconbutton";
import {EditIcon} from "../../ui/icons";

type Props = {
  currentItemIds: string[];
  onSelect: (ids: string[]) => void;
  disabled?: boolean;
};

export default function CustomParametersEditor({currentItemIds, onSelect, disabled}: Props) {
  const {t} = useTranslation();
  const {add: addModal} = useModal();

  const {data: accountParamsData} = useGetAccountCustomParametersQuery();
  const accountParams = useMemo(
    () => accountParamsData?.parameters ?? [],
    [accountParamsData?.parameters]
  );

  const [applyAdd] = useAddCustomParameterMutation();
  const [applyUpdate] = useUpdateCustomParameterMutation();
  const [applyDelete] = useDeleteCustomParameterMutation();

  const handleCreate = useCallback((label: string) => {
    const modal = addModal({
      id: "CreateCustomParameter",
      size: "m",
      header: <CustomParameterEditModal.Header />,
      content: (
        <CustomParameterEditModal.Content
          parameter={{id: "", name: label}}
          onSave={(data) => {
            applyAdd({
              variables: {name: data.name},
              refetchQueries: ["GetAccountCustomParameters"],
              awaitRefetchQueries: true,
            }).then((result) => {
              if (result.data) {
                onSelect([...currentItemIds, result.data.parameter.id]);
              }
              modal.remove();
            });
          }}
        />
      ),
    }, true);
  }, [addModal, applyAdd, currentItemIds, onSelect]);

  const handleEdit = useCallback((parameterId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const param = accountParams.find(p => p.id === parameterId);
    if (!param) return;

    const modal = addModal({
      id: `EditCustomParameter_${param.id}`,
      size: "m",
      header: <CustomParameterEditModal.Header />,
      content: (
        <CustomParameterEditModal.Content
          parameter={param}
          onSave={(data) => {
            applyUpdate({
              variables: {parameterId: param.id, name: data.name},
              refetchQueries: ["GetAccountCustomParameters"],
              awaitRefetchQueries: true,
            }).then(() => {
              modal.remove();
            });
          }}
          onDelete={() => {
            applyDelete({
              variables: {parameterId: param.id},
              refetchQueries: ["GetAccountCustomParameters"],
              awaitRefetchQueries: true,
            }).then(() => {
              onSelect(currentItemIds.filter(id => id !== param.id));
              modal.remove();
            });
          }}
          onClose={() => modal.remove()}
        />
      ),
    }, true);
  }, [addModal, applyUpdate, applyDelete, accountParams, currentItemIds, onSelect]);

  const handleChange = useCallback((data: SingleValue<Option> | MultiValue<Option>) => {
    if (Array.isArray(data)) {
      const ids = data.map((item: Option) => item.value).filter((id): id is string => !!id);
      onSelect(ids);
    } else {
      onSelect([]);
    }
  }, [onSelect]);

  const paramOptions = useMemo(() => {
    return accountParams.map(p => ({value: p.id, label: p.name}));
  }, [accountParams]);

  const currentOptions = useMemo(() => {
    return paramOptions.filter(opt => currentItemIds.includes(opt.value!));
  }, [currentItemIds, paramOptions]);

  const CustomOption = (props: OptionProps<Option, boolean>) => {
    const {innerProps, data, isSelected, getStyles} = props;
    const paramId = data.value;
    const defaultStyles = getStyles("option", props) as React.CSSProperties;

    return (
      <div
        {...innerProps}
        style={{
          ...defaultStyles,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          cursor: "pointer",
        }}
      >
        <span>{data.label}</span>
        {paramId && (
          <IconButton
            icon={EditIcon}
            size="s"
            title={t("common.edit")}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleEdit(paramId, e)}
            style={{
              marginLeft: "8px",
              color: isSelected ? "white" : "var(--text-color-regular)",
            }}
          />
        )}
      </div>
    );
  };

  const CustomMultiValueLabel = (props: MultiValueGenericProps<Option, boolean>) => {
    const {data} = props;
    const paramId = data.value;

    return (
      <>
        <components.MultiValueLabel {...props}>
          {data.label}
        </components.MultiValueLabel>
        {paramId && (
          <IconButton
            icon={EditIcon}
            size="s"
            title={t("common.edit")}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleEdit(paramId, e);
            }}
          />
        )}
      </>
    );
  };

  return (
    <CreatableSelect
      isMulti={true}
      options={paramOptions}
      value={currentOptions}
      disabled={disabled}
      onChange={handleChange}
      onCreate={handleCreate}
      components={{
        Option: CustomOption,
        MultiValueLabel: CustomMultiValueLabel,
      }}
      noOptionsMessage={()=>t("components.CustomParametersEditor.noOptions")}
    />
  );
}